import { ReactNode, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import { useRotatingLoadingMessages } from '@components/rotating-loading-messages/use-rotating-loading-messages';
import { ExportConvertersContext } from '@components/table';
import { useEvent } from '@hooks/use-event';
import { isWithinMs, minutes, seconds } from '@utils/milliseconds';

import {
  useDisconnectGoogleDrive,
  useGoogleDriveConnection,
  useInvalidateGoogleDrive,
} from 'api/authentication/hooks';

import {
  exportCsvToExcel,
  exportCsvToGoogleSheet,
  getUploadedFileUrl,
  uploadFile,
} from './requests';

const OAUTH_POPUP_WIDTH = 504;
const OAUTH_POPUP_HEIGHT = 648;

const openOAuthPopup = (url: string) => {
  const left = window.screenX + (window.outerWidth - OAUTH_POPUP_WIDTH) / 2;
  const top = window.screenY + (window.outerHeight - OAUTH_POPUP_HEIGHT) / 2;
  window.open(
    url,
    'oauth_popup',
    `width=${OAUTH_POPUP_WIDTH},height=${OAUTH_POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
  );
};

export const useUploadFile = (path: string, overwriteFile: boolean) => {
  return useMutation({
    mutationFn: (file: File) => uploadFile({ path, file, overwriteFile }),
  });
};

export const useUploadedFileUrlFromPath = (path: string) => {
  return useQuery({
    queryKey: ['uploaded-file-url', path],
    enabled: Boolean(path),
    staleTime: minutes(60),
    queryFn: () => getUploadedFileUrl(path),
  });
};

export const useExportCsvToExcel = () => {
  return useEvent(async (csvContent: string) => {
    await exportCsvToExcel(csvContent);
  });
};

export const useCreateGoogleSheetFromCsv = (filename?: string) => {
  const [isExporting, setIsExporting] = useState(false);
  useRotatingLoadingMessages({
    isEnabled: isExporting,
    title: 'Exporting to Google Sheets',
    firstMessage: 'Preparing your sheet...',
    minimumDelay: 500,
  });

  return useEvent(async (csvContent: string) => {
    try {
      setIsExporting(true);
      const result = await exportCsvToGoogleSheet(csvContent, filename);
      if (result.spreadsheetUrl) return result.spreadsheetUrl;
      if (result.needsAuth && result.authUrl) {
        openOAuthPopup(result.authUrl);
        throw new Error('Google Drive authorization required. Please authorize and retry export.');
      }
      throw new Error('Failed to create Google Sheet');
    } finally {
      setIsExporting(false);
    }
  });
};

export const useTableExportConverters = () => {
  const invalidateGoogleDrive = useInvalidateGoogleDrive();
  const convertToExcel = useExportCsvToExcel();
  const connectQuery = useGoogleDriveConnection();
  const convertToExportUrl = useCreateGoogleSheetFromCsv();
  const disconnectMutation = useDisconnectGoogleDrive();
  const isConnected = Boolean(connectQuery.data?.isConnected);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'oauth_callback_result' && event.newValue) {
        try {
          const result = JSON.parse(event.newValue);
          if (result.success && result.timestamp && isWithinMs(result.timestamp, seconds(10))) {
            invalidateGoogleDrive();
            localStorage.removeItem('oauth_callback_result');
          }
        } catch {
          // Invalid JSON
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [invalidateGoogleDrive]);

  const connectSheets = useEvent(() => {
    const authUrl = connectQuery.data?.authUrl;
    if (authUrl) {
      openOAuthPopup(authUrl);
    }
  });

  const disconnectSheets = useEvent(() => {
    disconnectMutation.mutate();
  });

  return {
    convertToExcel,
    convertToSheets: isConnected ? convertToExportUrl : undefined,
    connectSheets: isConnected ? undefined : connectSheets,
    disconnectSheets: isConnected ? disconnectSheets : undefined,
  };
};

export const ExportConvertersProvider = ({ children }: { children: ReactNode }) => {
  const converters = useTableExportConverters();
  return (
    <ExportConvertersContext.Provider value={converters}>
      {children}
    </ExportConvertersContext.Provider>
  );
};
